import { useState } from 'react';
import { ExternalLink, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { InvoiceRunStatus } from '@/graphql/generatedTypes';
import { Routes } from '@/utils/routes';
import { cn } from '@/lib/utils';

import type { InvoiceRunDetailQuery } from './InvoiceRunDetail.generated';
import { useRegenerateCollectionDraftMutation } from './RegenerateCollectionDraft.generated';
import { GatedButton } from './capability';
import { StatusDot } from './RunPills';
import type { InvoicingCapability } from './useInvoicingCapability';
import {
  formatDateTimeShort,
  isRegenerationOpen,
  runPresentationState,
  triggeredByLabel,
} from './invoiceRunPresentation';

type RunDetail = NonNullable<InvoiceRunDetailQuery['invoiceRun']>;
type CollectionInvoice = RunDetail['collection_invoices'][number];
type Regeneration = CollectionInvoice['regenerations'][number];

const STATUS_LABEL: Record<InvoiceRunStatus, string> = {
  [InvoiceRunStatus.Pending]: 'Queued',
  [InvoiceRunStatus.Running]: 'Regenerating',
  [InvoiceRunStatus.Done]: 'Regenerated',
  [InvoiceRunStatus.Failed]: 'Regeneration failed',
};

function outcomeLabel(regeneration: Regeneration): string | null {
  if (regeneration.status !== InvoiceRunStatus.Done) return null;
  return regeneration.updated_in_place
    ? 'draft updated in place'
    : 'new draft created (there was none)';
}

function RegenerationLine({ regeneration }: { regeneration: Regeneration }) {
  // Pending rows have no due_at: they present as Starting, like a charge run.
  const state = runPresentationState(regeneration);
  const when =
    regeneration.finished_at ??
    regeneration.started_at ??
    regeneration.enqueued_at;
  const outcome = outcomeLabel(regeneration);
  return (
    <div className="tw:flex tw:flex-col tw:gap-0.5 tw:text-xs">
      <span className="tw:inline-flex tw:items-center tw:gap-1.5 tw:text-foreground">
        <StatusDot state={state} />
        {STATUS_LABEL[regeneration.status]}
        <span className="tw:text-muted-foreground">
          · {formatDateTimeShort(when)} ·{' '}
          {triggeredByLabel(regeneration.requested_by)}
          {outcome ? ` · ${outcome}` : ''}
        </span>
      </span>
      {regeneration.error && (
        <span
          className="tw:line-clamp-3 tw:whitespace-pre-wrap tw:text-destructive"
          title={regeneration.error}
        >
          {regeneration.error}
        </span>
      )}
    </div>
  );
}

function RegenerateDialog({
  runId,
  collection,
  onOpenChange,
  onQueued,
}: {
  runId: string;
  collection: CollectionInvoice | null;
  onOpenChange: (open: boolean) => void;
  onQueued: () => void;
}) {
  const [regenerate] = useRegenerateCollectionDraftMutation();
  const [submitting, setSubmitting] = useState(false);
  if (!collection) return null;
  const name =
    collection.invoice_collection_name ?? collection.invoice_collection_id;

  const handleRegenerate = async () => {
    setSubmitting(true);
    try {
      await regenerate({
        variables: {
          generation_run_id: runId,
          invoice_collection_id: collection.invoice_collection_id,
        },
      });
      toast.success(`Regeneration of ${name} queued`);
      onQueued();
      onOpenChange(false);
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : 'Queueing the regeneration failed',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="tw:sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Regenerate the draft of {name}?</DialogTitle>
          <DialogDescription>
            The draft is rebuilt from the current partner and billing data.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="tw:flex tw:flex-col tw:gap-2 tw:text-sm">
          <ul className="tw:list-disc tw:space-y-1 tw:pl-5">
            <li>
              The existing Billomat draft is updated in place: all its line
              items, the title, intro, footer and reduction are replaced. Its
              invoice number (for Spain the ES number), date and tags stay.
            </li>
            <li>
              Without a draft for this period, a new one is created. An invoice
              that is no longer a draft is never changed — the regeneration
              fails instead.
            </li>
            <li>
              The breakdown files are recreated under the same names, so the
              result-file links of this run serve the new versions.
            </li>
            <li>
              It runs on the invoice worker and usually takes a few minutes. The
              charge run of this run waits until it is finished. If it fails
              after it started to rewrite the draft, the charge run waits until
              a new regeneration of this collection succeeds.
            </li>
          </ul>
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button disabled={submitting} onClick={handleRegenerate}>
            <RefreshCw className="tw:size-4" />
            Regenerate draft
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CollectionRow({
  collection,
  capability,
  blockedReason,
  onRegenerate,
}: {
  collection: CollectionInvoice;
  capability: InvoicingCapability;
  blockedReason: string | null | undefined;
  onRegenerate: () => void;
}) {
  const name =
    collection.invoice_collection_name ?? collection.invoice_collection_id;
  const open = collection.regenerations.some(isRegenerationOpen);
  const failedInRun = collection.result === 'ERROR';
  return (
    <div className="tw:grid tw:grid-cols-[minmax(200px,1fr)_minmax(260px,1.4fr)_160px] tw:items-start tw:gap-x-3 tw:border-b tw:border-border tw:px-4 tw:py-2.5 tw:last:border-b-0">
      <div className="tw:flex tw:flex-col tw:gap-1">
        <a
          href={Routes.invoiceCollection(collection.invoice_collection_id)}
          target="_blank"
          rel="noopener noreferrer"
          className="tw:group tw:flex tw:w-fit tw:items-center tw:gap-1 tw:text-sm tw:font-medium tw:text-foreground tw:no-underline tw:hover:underline"
        >
          {name}
          <ExternalLink className="tw:size-3 tw:shrink-0 tw:opacity-0 tw:transition-opacity tw:group-hover:opacity-100" />
        </a>
        <span className="tw:break-all tw:font-mono tw:text-xs tw:text-muted-foreground">
          {collection.invoice_collection_id}
        </span>
      </div>
      <div className="tw:flex tw:flex-col tw:gap-1.5">
        {collection.billomat_url ? (
          <a
            href={collection.billomat_url}
            target="_blank"
            rel="noopener noreferrer"
            className="tw:flex tw:w-fit tw:items-center tw:gap-1 tw:text-sm tw:text-foreground tw:hover:underline"
          >
            Billomat draft
            <ExternalLink className="tw:size-3" />
          </a>
        ) : (
          <span
            className={cn(
              'tw:text-sm',
              failedInRun ? 'tw:text-destructive' : 'tw:text-muted-foreground',
            )}
          >
            {failedInRun ? 'Failed in this run — no draft' : 'No draft'}
          </span>
        )}
        {collection.regenerations.map((regeneration) => (
          <RegenerationLine key={regeneration.id} regeneration={regeneration} />
        ))}
      </div>
      <div className="tw:flex tw:justify-end">
        {blockedReason ? (
          <span
            className="tw:text-right tw:text-xs tw:text-muted-foreground"
            title={blockedReason}
          >
            Locked
          </span>
        ) : (
          <GatedButton
            capability={capability}
            variant="outline"
            size="sm"
            className="tw:h-7"
            disabled={open}
            onClick={onRegenerate}
          >
            <RefreshCw className="tw:size-3.5" />
            {open ? 'In progress' : 'Regenerate draft'}
          </GatedButton>
        )}
      </div>
    </div>
  );
}

/**
 * The collection invoices a finished generation run left as drafts, each with
 * a "Regenerate draft" action and its regeneration history. Regenerating is
 * possible until the run's charge run starts.
 */
export function CollectionInvoicesCard({
  run,
  capability,
  onChanged,
}: {
  run: RunDetail;
  capability: InvoicingCapability;
  onChanged: () => void;
}) {
  const [selected, setSelected] = useState<CollectionInvoice | null>(null);
  const collections = run.collection_invoices;
  if (collections.length === 0) return null;
  const blockedReason = run.collection_draft_regeneration_blocked_reason;

  return (
    <Card className="tw:gap-0 tw:py-0">
      <CardHeader className="tw:flex tw:flex-col tw:gap-1 tw:px-4 tw:py-4">
        <CardTitle className="tw:text-base">Collection invoices</CardTitle>
        <p className="tw:text-sm tw:text-muted-foreground">
          {blockedReason
            ? `Drafts can no longer be regenerated: ${blockedReason}.`
            : 'Corrected partner or billing data? Regenerate a draft to rebuild it and its breakdown files in place, keeping its invoice number.'}
        </p>
      </CardHeader>
      <CardContent className="tw:px-0 tw:pb-0">
        <div className="tw:overflow-x-auto tw:border-t tw:border-border">
          <div className="tw:min-w-[640px]">
            {collections.map((collection) => (
              <CollectionRow
                key={collection.invoice_collection_id}
                collection={collection}
                capability={capability}
                blockedReason={blockedReason}
                onRegenerate={() => setSelected(collection)}
              />
            ))}
          </div>
        </div>
      </CardContent>
      <RegenerateDialog
        runId={run.id}
        collection={selected}
        onOpenChange={(isOpen) => {
          if (!isOpen) setSelected(null);
        }}
        onQueued={onChanged}
      />
    </Card>
  );
}
